import makeStyles from '@mui/styles/makeStyles';
import classNames from "classnames";
import {ChevronLeft, ChevronRight} from "lucide-react";
import {get, map} from 'lodash';
import React, {useState} from 'react';
import SwipeableViews from "components/SwipeableViews";
import {useTranslation} from "react-i18next";
import {convertUrl} from "common/helpers";
import {color, font, radius, type} from "common/tokens";
import {useSetTranslation} from "common/useSetTranslation";
import Card from "components/redesign/Card";
import ShimmerImage from "components/redesign/ShimmerImage";

const useStyles = makeStyles({
  carousel: {
    position: 'relative',
  },
  card: {
    overflow: 'hidden',
  },
  image: {
    width: '100%',
    height: 340,
    background: color.SURFACE_MUTED,
  },
  link: {
    ...type.meta,
    fontWeight: font.BOLD,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: '14px 20px',
    background: color.ACCENT_TINT,
    color: color.ACCENT_TEXT,
    textDecoration: 'none',
  },
  arrow: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    width: 40,
    height: 40,
    borderRadius: radius.PILL,
    border: `1px solid ${color.CARD_BORDER}`,
    background: color.SURFACE,
    color: color.TEXT,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: 0,
    zIndex: 1,
    '&:hover': {
      background: color.SURFACE_MUTED,
    },
  },
  arrowLeft: {
    left: -20,
  },
  arrowRight: {
    right: -20,
  },
  dots: {
    display: 'flex',
    justifyContent: 'center',
    gap: 6,
    marginTop: 14,
  },
  dot: {
    width: 7,
    height: 7,
    borderRadius: radius.PILL,
    background: color.DIVIDER,
    border: 'none',
    padding: 0,
    cursor: 'pointer',
  },
  dotCurrent: {
    background: color.ACCENT,
  },
});

const SwipeCards = ({cards = []}) => {
  const classes = useStyles();
  const {t} = useTranslation();
  const {st} = useSetTranslation();

  const [cardIndex, setCardIndex] = useState(0);
  const lastIndex = cards.length - 1;

  const step = by => {
    const next = cardIndex + by;
    setCardIndex(next > lastIndex ? 0 : next < 0 ? lastIndex : next);
  };

  const cardText = card => st(get(card, 'text'), get(card, 'localeText')) || t('readMore');

  return (
    <div>
      <div className={classes.carousel}>
        {cards.length > 1 && (
          <button
            type={'button'}
            aria-label={t('previous')}
            className={classNames(classes.arrow, classes.arrowLeft)}
            onClick={() => step(-1)}
          >
            <ChevronLeft size={20}/>
          </button>
        )}

        <SwipeableViews index={cardIndex} enableMouseEvents onChangeIndex={setCardIndex}>
          {map(cards, (card, index) => (
            <Card key={index} variant={'media'} className={classes.card}>
              <ShimmerImage src={convertUrl(card.image)} className={classes.image}/>
              {Boolean(get(card, 'link')) && (
                <a
                  href={card.link}
                  target={'_blank'}
                  rel={'noopener noreferrer'}
                  className={classes.link}
                >
                  {cardText(card)}
                  <ChevronRight size={16}/>
                </a>
              )}
            </Card>
          ))}
        </SwipeableViews>

        {cards.length > 1 && (
          <button
            type={'button'}
            aria-label={t('nextStep')}
            className={classNames(classes.arrow, classes.arrowRight)}
            onClick={() => step(1)}
          >
            <ChevronRight size={20}/>
          </button>
        )}
      </div>

      {cards.length > 1 && (
        <div className={classes.dots}>
          {map(cards, (card, index) => (
            <button
              key={index}
              type={'button'}
              aria-label={`${index + 1}`}
              className={classNames(classes.dot, index === cardIndex && classes.dotCurrent)}
              onClick={() => setCardIndex(index)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default SwipeCards;
